import React from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { OctagonAlert, TriangleAlert, Info, ArrowUpRight, Power, Loader2, LifeBuoy, WandSparkles, EyeOff } from 'lucide-react';
import { cn } from '@/lib/utils';
import { switchSlotOn, switchDeviceOn, applyFix, dismissRule } from '../../lib/doctorCalls';
import { fixTarget, settingsHref, fixErrorView, blockedToast, notesNotShown, answerFindings, DOCTOR_WORDS } from '../../lib/doctorView';
import { DOCTOR_TAG, doctorUiOn } from '../../lib/doctorSwitch';
import { DOCTOR_ME_KEY } from '../../hooks/useDoctorMe';
import { DOCTOR_ROOT } from '../../lib/doctorKeys';
import { PAUSE_EFFECT_NOTE, PAUSE_DISABLED_NOTE, NOT_PROVISIONED_NOTE } from '../../lib/slotState';

/*
 * r16sd: the small pieces every Settings Doctor surface shares - the level look (red / amber / info on the semantic tokens), the
 * attention dot and the fix control (DOCTOR-API 1.4). The words on them are always the server's (hold / finding text, fix label).
 */

export const LEVEL = {
  red: {
    icon: OctagonAlert, text: 'text-danger', dot: 'bg-danger', ring: 'ring-danger/30', bar: 'bg-danger',
    box: 'border-danger/35 bg-danger/10', chip: 'bg-danger/15 text-danger ring-1 ring-danger/25',
  },
  amber: {
    icon: TriangleAlert, text: 'text-warning', dot: 'bg-warning', ring: 'ring-warning/30', bar: 'bg-warning',
    box: 'border-warning/35 bg-warning/10', chip: 'bg-warning/15 text-warning ring-1 ring-warning/25',
  },
  info: {
    icon: Info, text: 'text-info', dot: 'bg-info', ring: 'ring-info/30', bar: 'bg-info/70',
    box: 'border-border bg-secondary/40', chip: 'bg-info/15 text-info ring-1 ring-info/25',
  },
};
/** A finding's level in the hold scale: BLOCK = red, WARN = amber, INFO = info. */
export const findingTone = (level) => (level === 'BLOCK' ? 'red' : level === 'INFO' ? 'info' : 'amber');
export const tone = (level) => LEVEL[level] || LEVEL.amber;

/** The attention dot: red / amber, a halo so it reads on any row colour. */
export function AttentionDot({ level, className, testid, title }) {
  const t = tone(level);
  return (
    <span
      className={cn('inline-block h-2.5 w-2.5 shrink-0 rounded-full ring-2 ring-offset-0', t.dot, t.ring, className)}
      role="img"
      aria-label={title || (level === 'red' ? 'Needs attention now' : 'Needs a look')}
      title={title}
      data-testid={testid}
      data-level={level}
    />
  );
}

/** The level icon of a hold or a finding. */
export function LevelIcon({ level, className }) {
  const t = tone(level);
  const Icon = t.icon;
  return <Icon className={cn('h-4 w-4 shrink-0', t.text, className)} aria-hidden />;
}

/** After a fix or a dismiss changed what the doctor says: every cached view of it is re-read (lists, panels, /me, the settings). */
function invalidateAfterFix(queryClient, slotName) {
  queryClient.invalidateQueries({ queryKey: ['slots'] });
  queryClient.invalidateQueries({ queryKey: DOCTOR_ROOT });
  queryClient.invalidateQueries({ queryKey: DOCTOR_ME_KEY });
  if (slotName) {
    queryClient.invalidateQueries({ queryKey: ['slotSettings', slotName] });
    queryClient.invalidateQueries({ queryKey: ['settingsHistory', slotName] });
  }
}

/**
 * One fix, the way DOCTOR-API 1.4 says to offer it: a "Switch on" button (the existing pause PUT); stage 5 (review SF1): a "set"
 * fix or "Use my account email" as a one-click button - POST .../doctor/fix {rule, fix}, the server derives and saves the values
 * (an undo record in History) - while the fixApply switch is on and the page has no unsaved edits (`applyBlocked`); otherwise a
 * link that opens the setting (on the settings page itself, a button that brings it into view); or the label as text (a hint such
 * as "Contact support to top up"; review SF9: not drawn when `nearText`, the hold's own sentence, already says it).
 * `settings` = the finding's setting numbers (where a fix with no setting of its own points); `rule` = the hold's / finding's rule.
 */
export function DoctorFix({
  fix, settings, slotName, deviceId, onFocusSetting, onDone, size = 'sm', testid, rule, applyBlocked, nearText,
}) {
  const queryClient = useQueryClient();
  const canApply = !!(rule && slotName && !applyBlocked && doctorUiOn('fixApply'));
  const target = fixTarget(fix, settings, { apply: canApply });
  const name = target.action === 'switchOnDevice' ? deviceId : slotName;
  const sw = useMutation({
    mutationFn: () => (target.action === 'switchOnDevice' ? switchDeviceOn(deviceId) : switchSlotOn(slotName)),
    onSuccess: () => {
      // eslint-disable-next-line no-console
      console.info(`${DOCTOR_TAG} fix ${target.action} slot=${slotName}${target.action === 'switchOnDevice' ? ` device=${deviceId}` : ''}: done`);
      queryClient.invalidateQueries({ queryKey: ['slots'] });
      queryClient.invalidateQueries({ queryKey: ['devices'] });
      queryClient.invalidateQueries({ queryKey: DOCTOR_ROOT });
      queryClient.invalidateQueries({ queryKey: DOCTOR_ME_KEY });
      // review SF13: the pause switch's own note (a running session finishes first)
      toast.success(`${name} switched on`, { description: PAUSE_EFFECT_NOTE });
      if (onDone) onDone();
    },
    onError: (e) => {
      const d = e && e.response && e.response.data && typeof e.response.data === 'object' ? e.response.data : null;
      const code = d && typeof d.error === 'string' ? d.error : null;
      // eslint-disable-next-line no-console
      console.info(`${DOCTOR_TAG} fix ${target.action} slot=${slotName}: refused (${code || (e && e.response ? `HTTP ${e.response.status}` : 'no answer')})`);
      // review SF13: the codes in words, the way the Slots page's pause switch says them (never the raw code)
      if (code === 'slot_not_provisioned') toast.error(`${name} is not provisioned`, { description: NOT_PROVISIONED_NOTE });
      else if (code === 'feature_disabled') toast.error(PAUSE_DISABLED_NOTE);
      else toast.error(`Could not update ${name}`, { description: (d && typeof d.message === 'string' && d.message) || undefined });
    },
  });
  const ap = useMutation({
    mutationFn: () => applyFix(slotName, rule, fix && fix.id),
    onSuccess: (res) => {
      const d = res && res.data && typeof res.data === 'object' ? res.data : {};
      const n = Number(d.changed) || 0;
      // eslint-disable-next-line no-console
      console.info(`${DOCTOR_TAG} fix ${rule}/${fix && fix.id} slot=${slotName}: applied (changed ${n}${d.changeId ? `, change ${d.changeId}` : ''})`);
      invalidateAfterFix(queryClient, slotName);
      toast.success(`${DOCTOR_WORDS.done} ${target.label}`, {
        description: n > 0 ? `${n} setting${n === 1 ? '' : 's'} saved (undo available in History)` : undefined,
      });
      // what the server's save said besides the doctor's own findings (the panel, re-read, shows those): SD35 rewrites, SD37
      const notes = (Array.isArray(d.notes) ? d.notes : []).filter((t) => typeof t === 'string' && t.trim());
      notesNotShown(notes, answerFindings(d) || []).forEach((t) => (toast.warning || toast.info)(t));
      const refused = blockedToast(d.blocked, d);
      if (refused) (toast.warning || toast.info)(refused.text, { description: refused.description || undefined, duration: 15000 });
      if (onDone) onDone(d);
    },
    onError: (e) => {
      const v = fixErrorView(e);
      // eslint-disable-next-line no-console
      console.info(`${DOCTOR_TAG} fix ${rule}/${fix && fix.id} slot=${slotName}: refused (${v.code || (v.status ? `HTTP ${v.status}` : 'no answer')})`);
      if (v.missing) {
        // a server without the fix route: the setting opens instead (the customer changes it by hand)
        if (target.focusId !== null && target.focusId !== undefined && onFocusSetting) { onFocusSetting(target.focusId); return; }
        toast.error('That could not be saved just now. Try again in a minute.');
        return;
      }
      if (v.gone) {
        queryClient.invalidateQueries({ queryKey: DOCTOR_ROOT });
        queryClient.invalidateQueries({ queryKey: ['slots'] });
        toast.info(v.text);
        return;
      }
      if (v.code === 'needs_support') (toast.warning || toast.info)(v.text, { duration: 12000 });
      else toast.error(v.text);
    },
  });
  const tid = testid || `doctor-fix-${fix && fix.id}`;
  const base = cn(
    'tap inline-flex max-w-full items-center gap-1.5 rounded-full font-medium transition-colors',
    size === 'xs' ? 'px-2.5 py-1 text-[11px]' : 'px-3 py-1.5 text-xs',
  );

  if (target.mode === 'apply') {
    const ids = fix && fix.set ? Object.keys(fix.set).map((k) => `#${k}`).join(' ') : '';
    return (
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); ap.mutate(); }}
        disabled={ap.isPending}
        className={cn(base, 'bg-primary text-primary-foreground shadow-glow hover:bg-primary/90 disabled:opacity-50')}
        title={ids || undefined}
        data-testid={tid}
        data-fix-mode="apply"
      >
        {ap.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden /> : <WandSparkles className="h-3.5 w-3.5" aria-hidden />}
        <span className="truncate">{target.label}</span>
      </button>
    );
  }

  if (target.mode === 'button') {
    const missing = target.action === 'switchOnDevice' ? !deviceId : !slotName;
    return (
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); sw.mutate(); }}
        disabled={missing || sw.isPending}
        className={cn(base, 'bg-primary text-primary-foreground shadow-glow hover:bg-primary/90 disabled:opacity-50')}
        data-testid={tid}
        data-fix-mode="button"
      >
        {sw.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden /> : <Power className="h-3.5 w-3.5" aria-hidden />}
        <span className="truncate">{target.label}</span>
      </button>
    );
  }
  if (target.mode === 'link') {
    const cls = cn(base, 'bg-primary/10 text-primary ring-1 ring-primary/25 hover:bg-primary/20');
    const inner = (
      <>
        <span className="truncate">{target.label}</span>
        <ArrowUpRight className="h-3.5 w-3.5 shrink-0" aria-hidden />
      </>
    );
    const title = `Opens setting ${target.focusId}`;
    if (onFocusSetting) {
      return (
        <button type="button" onClick={(e) => { e.stopPropagation(); onFocusSetting(target.focusId); }} className={cls} title={title}
          data-testid={tid} data-fix-mode="link" data-focus={target.focusId}>
          {inner}
        </button>
      );
    }
    return (
      <Link to={settingsHref(slotName, target.focusId)} className={cls} title={title} onClick={(e) => e.stopPropagation()}
        data-testid={tid} data-fix-mode="link" data-focus={target.focusId}>
        {inner}
      </Link>
    );
  }
  if (target.mode === 'hint') {
    // review SF9: the hold's own sentence already ends "... contact support to top up." - the hint is not repeated under it
    if (typeof nearText === 'string' && target.label && nearText.toLowerCase().includes(target.label.toLowerCase())) return null;
    // text, not a button: nothing happens on a click (DOCTOR-API 1.4 "hint")
    return (
      <span className={cn('inline-flex max-w-full items-center gap-1.5 font-medium text-foreground/80', size === 'xs' ? 'text-[11px]' : 'text-xs')}
        data-testid={tid} data-fix-mode="hint">
        <LifeBuoy className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
        <span className="min-w-0">{target.label}</span>
      </span>
    );
  }
  return null;
}

/**
 * Stage 5 (review SF1): "Don't show again" on one finding - POST .../doctor/dismiss {rule}; the server hides every current finding of
 * the rule on this slot until its settings change, and the drawer lists it under "Hidden". Drawn while the dismiss switch is on.
 */
export function DismissFinding({ rule, slotName, onDone, testid }) {
  const queryClient = useQueryClient();
  const on = !!(rule && slotName && doctorUiOn('dismiss'));
  const m = useMutation({
    mutationFn: () => dismissRule(slotName, rule),
    onSuccess: (res) => {
      const d = res && res.data && typeof res.data === 'object' ? res.data : {};
      // eslint-disable-next-line no-console
      console.info(`${DOCTOR_TAG} dismiss ${rule} slot=${slotName}: hidden (${Number.isFinite(Number(d.openFindings)) ? `${Number(d.openFindings)} open` : 'done'})`);
      queryClient.invalidateQueries({ queryKey: DOCTOR_ROOT });
      queryClient.invalidateQueries({ queryKey: ['slots'] });
      if (onDone) onDone(d);
    },
    onError: (e) => {
      const v = fixErrorView(e);
      // eslint-disable-next-line no-console
      console.info(`${DOCTOR_TAG} dismiss ${rule} slot=${slotName}: refused (${v.code || (v.status ? `HTTP ${v.status}` : 'no answer')})`);
      if (v.gone) { queryClient.invalidateQueries({ queryKey: DOCTOR_ROOT }); toast.info(v.text); return; }
      toast.error(v.missing ? 'That could not be saved just now. Try again in a minute.' : v.text);
    },
  });
  if (!on) return null;
  return (
    <button type="button" onClick={(e) => { e.stopPropagation(); m.mutate(); }} disabled={m.isPending}
      className="tap inline-flex items-center gap-1.5 rounded-full px-2.5 py-1.5 text-xs font-medium text-muted-foreground hover:bg-secondary/70 hover:text-foreground disabled:opacity-50"
      data-testid={testid || `doctor-dismiss-${rule}`}>
      {m.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden /> : <EyeOff className="h-3.5 w-3.5" aria-hidden />}
      {DOCTOR_WORDS.dontShow}
    </button>
  );
}

/**
 * "since Oct 26, 11:40" / "until Oct 28, 09:00" under a hold - only when its text names no time of its own: "Paused by you on 10-26
 * 15:32" is the server's clock in the slot's time zone, and a second time in the browser's zone under it would read as two events.
 */
export const textHasTime = (t) => /\b\d{1,2}:\d{2}\b/.test(String(t || ''));
export function HoldWhen({ hold, fmt, className }) {
  if (!hold || textHasTime(hold.text)) return null;
  const parts = [];
  if (Number.isFinite(hold.since)) parts.push(`since ${fmt(hold.since)}`);
  if (Number.isFinite(hold.until)) parts.push(`until ${fmt(hold.until)}`);
  if (!parts.length) return null;
  return <span className={cn('text-[11px] tnum text-muted-foreground', className)}>{parts.join(' · ')}</span>;
}

/** Setting numbers as small chips (#602 #601). */
export function SettingChips({ settings, className }) {
  if (!Array.isArray(settings) || !settings.length) return null;
  return (
    <span className={cn('flex flex-wrap gap-1', className)}>
      {settings.slice(0, 8).map((s) => (
        <span key={s} className="rounded bg-secondary/70 px-1.5 py-0.5 font-mono text-[10px] text-foreground/80 ring-1 ring-border">#{s}</span>
      ))}
    </span>
  );
}
